import * as ko from 'knockout';
import {PLEASE_WAIT_MESSAGE} from '../../constants/Language';
import {IOverride, ISyncField} from '../../index';
import {nsGroupMemberOverride} from '../group/members/nsGroupMemberOverride';
import {IOverrideValueComparison} from '../override/IOverrideDetailsRow';
import {nsOverride} from '../override/nsOverride';
import {SOURCE_DATA_OVERRIDE_DETAILS_KEYS} from '../override/overrideDetailsKeys';
import {EMPTY_STRING} from '../../constants/Language';
import {lm} from '../../appContext';
import canMutateMembershipOverride = nsGroupMemberOverride.canMutateMembershipOverride;

/** Row VM dependencies on source-data {@link viewModel}. */
export interface ISyncfieldListParent {
    showOverrideColumn(): boolean;
    getOverride(sf: ISyncField): IOverride | undefined;
    updateOverrideComment(overrideId: number, comment: string): Promise<void>;
}

/** Expandable grid row for one syncfield on the source-data panel. */
export class SyncfieldRowVm {
    readonly isExpanded = ko.observable(false);
    readonly isEditingOverrideComment = ko.observable(false);
    readonly isSavingOverrideDetails = ko.observable(false);
    readonly overrideCommentDraft = ko.observable('');
    readonly overridePanelRevision = ko.observable(0);
    readonly overrideDetailsWaitMessage = PLEASE_WAIT_MESSAGE;
    readonly overrideDetailsKeys = SOURCE_DATA_OVERRIDE_DETAILS_KEYS;

    constructor(
        syncfield: ISyncField,
        readonly parent: ISyncfieldListParent,
    ) {
        this.syncfield = syncfield;
    }

    syncfield: ISyncField;

    /** Refresh row data after object reload while keeping expand/edit UI state. */
    setSyncfield(syncfield: ISyncField): void {
        this.syncfield = syncfield;
        this.bumpOverridePanelRevision();
    }

    getRowBindings() {
        return {
            attr: {'aria-expanded': this.isExpanded},
        };
    }

    hasOverride(): boolean {
        return !!this.getOverride();
    }

    /** Expand only when the override column (and chevrons) are visible. */
    canToggleExpand(): boolean {
        return this.parent.showOverrideColumn() && this.hasOverride();
    }

    doExpandToggle = (): void => {
        if (this.canToggleExpand()) {
            this.isExpanded(!this.isExpanded());
        }
    };

    collapse(): void {
        this.isExpanded(false);
    }

    getOverride() {
        return this.parent.getOverride(this.syncfield) ?? null;
    }

    /** Display params for shared `component/override/override-status`. */
    getOverrideStatusParams() {
        const override = this.getOverride();
        if (!override) {
            return null;
        }
        const display = nsOverride.getSyncfieldOverrideDisplay(override);
        return {
            translationKey: display.translationKey,
            pillVariant: display.pillVariant,
            hasComment: !!override.comment?.trim(),
        };
    }

    /** Same CMS key as the status pill (will override / is overridden / will restore). */
    getOverrideSectionHeadingKey(): string | null {
        return this.getOverrideStatusParams()?.translationKey ?? null;
    }

    /**
     * Value pair for the expanded panel.
     * Active override: original → overridden; pending restore: overridden → original.
     */
    getOverrideValueComparison(): IOverrideValueComparison | null {
        if (!this.getOverride()) {
            return null;
        }
        const empty = lm.t(EMPTY_STRING) ?? '';
        const originalRaw = this.syncfield.value ?? '';
        const overriddenRaw = this.overrideValueText();
        const original = originalRaw === '' ? empty : originalRaw;
        const overridden = overriddenRaw === '' ? empty : overriddenRaw;
        if (this.isOverrideDeleted()) {
            return {
                fromLabelKey: 'overridden value',
                toLabelKey: 'original value',
                from: overridden,
                to: original,
            };
        }
        return {
            fromLabelKey: 'original value',
            toLabelKey: 'overridden value',
            from: original,
            to: overridden,
        };
    }

    overrideIdText(): string {
        const id = this.getOverride()?.id;
        return id == null ? '' : String(id);
    }

    overrideValueText(): string {
        return this.getOverride()?.value ?? '';
    }

    overrideSyncImportFileIdText(): string {
        const id = this.getOverride()?.syncronizedimportfileid;
        return id ? String(id) : '';
    }

    overrideCommentText(): string {
        return this.getOverride()?.comment ?? '';
    }

    overrideOverriddenWhen(): string {
        return this.getOverride()?.overriddenwhen ?? '';
    }

    overrideOverriddenByName(): string {
        return this.getOverride()?.overriddenby ?? '';
    }

    overrideDeletedWhen(): string {
        return this.getOverride()?.deletedwhen ?? '';
    }

    overrideDeletedByName(): string {
        return this.getOverride()?.deletedby ?? '';
    }

    isOverrideDeleted(): boolean {
        return !!this.getOverride()?.deletedbyid;
    }

    /** Soft-delete awaiting sync — cancel-pending-restore is available. */
    isOverridePendingDeletion(): boolean {
        return nsOverride.isOverridePendingDeletion(this.getOverride());
    }

    canEditOverrideComment(): boolean {
        const override = this.getOverride();
        return !!override && canMutateMembershipOverride(override);
    }

    startEditOverrideComment = (): void => {
        this.overrideCommentDraft(this.overrideCommentText());
        this.isEditingOverrideComment(true);
    };

    cancelEditOverrideComment = (): void => {
        this.isEditingOverrideComment(false);
        this.overrideCommentDraft(this.overrideCommentText());
    };

    submitOverrideComment = (): void => {
        const override = this.getOverride();
        if (!override?.id) {
            return;
        }
        this.isEditingOverrideComment(false);
        this.isSavingOverrideDetails(true);
        void this.parent
            .updateOverrideComment(override.id, this.overrideCommentDraft())
            .finally(() => this.isSavingOverrideDetails(false));
    };

    bumpOverridePanelRevision(): void {
        this.overridePanelRevision(this.overridePanelRevision() + 1);
    }
}
